import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Forms, Loading, SystemText, Tabs, SaveButton, DebugInfo } from '@salesperformanceint/e4shared';

import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';

import { apiGet, apiPost, apiDownloadFile } from 'services';

import Enrollments from './components/enrollments';
import { e4Format } from 'helpers/dates';
import Schedule from './components/schedule';
import { setAsideFilters, setShowSuccess, toggleFilterAside } from 'actions/ui';
import SearchField from 'components/general/searchField';
import Summary from './components/summary';

import posthog from 'posthog-js';
import { hasModule } from 'helpers/profile';

export const PathwayTracker = () => {
    const dispatch = useDispatch();

    const [loading, setLoading] = React.useState(true);
    const [loadingSummary, setLoadingSummary] = React.useState(true);
    const [searchFilter, setSearchFilter] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState('completed');
    const [descending, setDescending] = React.useState(true);
    const [recordCount, setRecordCount] = React.useState(0);
    const [data, setData] = React.useState(null);
    const [selected, setSelected] = React.useState([]);
    const [selectAll, setSelectAll] = React.useState(false);
    const [tab, setTab] = React.useState('details');
    const [pathway, setPathway] = React.useState(null);
    const [selectedPathway, setSelectedPathway] = React.useState(null);
    const [showAssign, setShowAssign] = React.useState(false);
    const [users, setUsers] = React.useState([]);
    const [availablePathways, setAvailablePathways] = React.useState([]);
    const [pathways, setPathways] = React.useState([]);
    const [downloadingReport, setDownloadingReport] = React.useState(false);
    const { filterAsideOpen, asideFilters } = useSelector(state => state.ui);
    const { user } = useSelector(state => state.user);
    const allowCoaches = user.companySettings.development.allowCoaches;

    const api = {
        loadUsers: async () => {
            if (pathway) {
                await apiGet(`pathway-users/${pathway}`, user.guid, response => {
                    setUsers(
                        response.map(u => ({
                            ...u,
                            disabled: u.date_Assigned && !u.date_Completed,
                        }))
                    );
                });
            }
        },
        loadAvailablePathways: async () => {
            await apiGet('pathway-list-available', user.guid, response => {
                setAvailablePathways(response);
            });
        },
        loadPathways: async () => {
            setLoadingSummary(true);
            const filters = [{ property: 'search', value: searchFilter }];

            await apiPost(
                'pathway-list',
                filters,
                user.guid,
                response => {
                    setPathways(response);
                    setLoadingSummary(false);
                },
                null,
                'POST',
                'json'
            );
        },
        loadPathwayDashboard: async callback => {
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            const qs = `?page=${page}&sort=${sortBy}&desc=${descending}`;

            await apiPost(
                `pathway-dashboard${qs}`,
                filters,
                user.guid,
                response => {
                    setRecordCount(response.recordcount);
                    setData(response);
                    setLoading(false);
                    callback?.();
                },
                null,
                'POST',
                'json'
            );
        },
        assign: (details, callback) => {
            details.id = pathway;
            details.assigned_Ids = details.ids;
            apiPost('pathway-enroll', details, user.guid, () => {
                dispatch(setShowSuccess(SystemText('PathwayTracker/Schedule/Success')));
                setShowAssign(false);
                callback?.();
                api.loadUsers();
                api.loadPathways();
                api.loadPathwayDashboard();
            });
        },
        unassign: (confirm, ids, callback) => {
            if (confirm) {
                const details = {
                    assigned_Ids: ids,
                };

                apiPost('pathway-unenroll', details, user.guid, () => {
                    dispatch(setShowSuccess(SystemText('PathwayTracker/Schedule/UnenrollSuccess')));
                    setLoading(true);
                    setSelected([]);
                    setSelectAll(false);
                    api.loadPathways();
                    api.loadPathwayDashboard();
                });
            }

            callback?.();
        },
        updateDueDate: (due, callback) => {
            const pathway = {
                assigned_Ids: selected,
                date_Due: due,
            };

            apiPost('pathway-update-due', pathway, user.guid, () => {
                dispatch(setShowSuccess(SystemText('PathwayTracker/Schedule/EditSuccess')));
                api.loadPathwayDashboard();
            });

            callback?.();
        },
        getAllPathwayData: () => apiGet('pathway-list', user.guid, response => setPathways(response)),
        report: () => {
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            apiDownloadFile(
                'pathway-report',
                filters,
                user.guid,
                () => {
                    setDownloadingReport(false);
                    posthog.capture('tracker', { trackerType: 'PathwayTracker', userEvent: 'export' });
                },
                null,
                'POST'
            );
        },
    };

    React.useEffect(() => {
        const promises = [];

        promises.push(api.loadAvailablePathways());
        promises.push(api.getAllPathwayData());
        promises.push(api.loadUsers());
        promises.push(api.loadPathways());
        promises.push(api.loadPathwayDashboard());

        if (!promises.length) {
            setLoading(false);
        } else {
            Promise.all(promises).then(() => {
                setLoading(false);
            });
        }
    }, []);

    React.useEffect(() => {
        let fields = [
            {
                property: 'pathway_Ids',
                name: SystemText('PathwayTracker/Filters/Pathway'),
                type: 'list',
            },
            {
                property: 'role_Ids',
                name: SystemText('PathwayTracker/Filters/Role'),
                type: 'list',
            },
            {
                property: 'department_Ids',
                name: SystemText('PathwayTracker/Filters/Department'),
                type: 'list',
            },
            {
                property: 'business_Unit_Ids',
                name: SystemText('PathwayTracker/Filters/BusinessUnit'),
                type: 'list',
            },
            {
                property: 'location_Ids',
                name: SystemText('PathwayTracker/Filters/Location'),
                type: 'list',
            },
            {
                property: 'language_Ids',
                name: SystemText('PathwayTracker/Filters/Language'),
                type: 'list',
            },
            {
                property: 'manager_Ids',
                name: SystemText('PathwayTracker/Filters/Manager'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'coach_Ids',
                name: SystemText('PathwayTracker/Filters/Coach'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'owner_Ids',
                name: SystemText('PathwayTracker/Filters/Owner'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'statuses',
                name: SystemText('PathwayTracker/Filters/Status'),
                type: 'list',
                stringValues: true,
                data: [
                    { id: 'O', name: SystemText('PathwayTracker/Filters/Status/Outstanding') },
                    { id: 'D', name: SystemText('PathwayTracker/Filters/Status/Overdue') },
                    { id: 'C', name: SystemText('PathwayTracker/Filters/Status/Complete') },
                ],
            },
            {
                property: 'date_Assigned',
                name: SystemText('PathwayTracker/Filters/Assigned'),
                type: 'range',
            },
            {
                property: 'date_Launch',
                name: SystemText('PathwayTracker/Filters/Launch'),
                type: 'range',
            },
            {
                property: 'date_Due',
                name: SystemText('PathwayTracker/Filters/Due'),
                type: 'range',
            },
            {
                property: 'date_Completed',
                name: SystemText('PathwayTracker/Filters/Completed'),
                type: 'range',
            },
        ];

        if (!allowCoaches) {
            fields = fields.filter(x => x.property !== 'coach_Ids');
        }

        dispatch(setAsideFilters(fields));
    }, []);

    React.useEffect(() => {
        if (!hasModule(user, 'pathways')) {
            history.replace('/forbidden');
        }
    }, [user]);

    React.useEffect(() => {
        api.loadUsers();
    }, [pathway]);

    const sortProps = {
        sortBy,
        descending,
        setSortBy,
        setDescending,
    };

    React.useEffect(() => {
        const temp = selectAll ? data.assignments.filter(x => !x.date_Completed).map(x => x.id) : [];
        setSelected(temp);
    }, [selectAll]);

    React.useEffect(() => {
        if (!filterAsideOpen) {
            setPage(1);
            setLoading(true);
            api.loadPathwayDashboard(() => setLoading(false));
        }
    }, [filterAsideOpen]);

    React.useEffect(() => {
        setPage(1);
        setLoading(true);
        api.loadPathways();
        api.loadPathwayDashboard(() => setLoading(false));
    }, [searchFilter, sortBy, descending]);

    React.useEffect(() => {
        setLoading(true);
        api.loadPathwayDashboard(() => setLoading(false));
    }, [page]);

    React.useEffect(() => {
        DebugInfo('component', 'PathwayTracker');
        posthog.capture('tracker', { trackerType: 'PathwayTracker', userEvent: 'view' });
    }, []);

    React.useEffect(() => {
        if (pathway && pathways?.length > 0) {
            const selectedPathway = pathways.find(p => p.id === pathway);
            if (selectedPathway) {
                setSelectedPathway(selectedPathway);
            } else {
                setSelectedPathway(null);
            }
        }
    }, [pathway, pathways]);

    const extraButtons = () => (
        <div className="flex flex-center">
            <div className="select-holder mr10">
                <Forms.Select
                    inline
                    mandatory
                    placeholder={SystemText('PathwayTracker/Filter/Fields/Pathway/Placeholder')}
                    data={availablePathways}
                    value={pathway}
                    onChange={setPathway}
                />
            </div>
            <button className="btn btn-success" onClick={() => setShowAssign(true)}>
                <i className="mr10 fa fa-users" />
                {SystemText('PathwayTracker/Buttons/Assign')}
            </button>
        </div>
    );

    const showFilterMenu = () => {
        dispatch(toggleFilterAside(true));
    };

    return (
        <div>
            <PageHeader title={SystemText('PathwayTracker/Title')} extra={extraButtons} tracker />
            <section className="content mtm10 tracker" data-testid="pathway-tracker-content">
                <Box>
                    <div className="mb10">
                        {tab === 'details' ? (
                            <SearchField
                                value={searchFilter}
                                onChange={setSearchFilter}
                                placeholder={SystemText('PathwayTracker/SearchDetails')}
                            ></SearchField>
                        ) : (
                            <SearchField
                                value={searchFilter}
                                onChange={setSearchFilter}
                                placeholder={SystemText('PathwayTracker/Search')}
                            >
                                {(user.administrator || user.isManager) && (
                                    <SaveButton
                                        className="btn btn-info mr10"
                                        saving={downloadingReport}
                                        setSaving={setDownloadingReport}
                                        onClick={api.report}
                                        disabled={loading}
                                    >
                                        <i className="fa fa-download mr5" />
                                        {SystemText('PathwayTracker/Buttons/Report')}
                                    </SaveButton>
                                )}
                                <button
                                    className="btn btn-success"
                                    id="search-filter-button"
                                    onClick={showFilterMenu}
                                    disabled={loading}
                                >
                                    <i className="fa fa-bars-filter mr5" id="search-filter-button-icon" />
                                    {SystemText('PathwayTracker/Buttons/Filter')}
                                </button>
                            </SearchField>
                        )}
                    </div>

                    <div>
                        <div className="pr5 pl5">
                            <Tabs
                                role="tabpanel"
                                tab={tab}
                                setTab={tab => setTab(tab)}
                                tabs={['details', 'enrollments']}
                                style="clean"
                                tabNames={{
                                    details: SystemText('PathwayTracker/Tabs/Details'),
                                    enrollments: SystemText('PathwayTracker/Tabs/Enrolments'),
                                }}
                                extras={null}
                            >
                                <div className="mt10">
                                    {tab === 'details' && loadingSummary ? (
                                        <Loading />
                                    ) : tab === 'details' && !loadingSummary ? (
                                        <Summary data={pathways} />
                                    ) : (
                                        tab === 'enrollments' &&
                                        (loading ? (
                                            <Loading />
                                        ) : !recordCount ? (
                                            <div>
                                                <NoResults
                                                    title={SystemText('PathwayTracker/NoResults/Title')}
                                                    message={SystemText('PathwayTracker/NoResults/Message')}
                                                />
                                            </div>
                                        ) : (
                                            <Enrollments
                                                data={data}
                                                pathways={pathways}
                                                page={page}
                                                loading={loading}
                                                setPage={setPage}
                                                selected={selected}
                                                setSelected={setSelected}
                                                selectAll={selectAll}
                                                setSelectAll={setSelectAll}
                                                {...sortProps}
                                                reload={() => {
                                                    setSelectAll(false);
                                                    setSelected([]);
                                                    api.loadPathwayDashboard();
                                                    api.loadUsers();
                                                }}
                                                unassign={api.unassign}
                                                updateDueDate={api.updateDueDate}
                                            />
                                        ))
                                    )}
                                </div>
                            </Tabs>
                        </div>
                    </div>
                </Box>
            </section>
            <Schedule
                show={showAssign}
                pathway={selectedPathway}
                users={users}
                close={() => setShowAssign(false)}
                onSchedule={api.assign}
            />
        </div>
    );
};

export default PathwayTracker;
